import { Controller } from "@hotwired/stimulus"
import Alertify from "alertifyjs"

export default class extends Controller {
  static targets = ["externalId", "valuerId", "propertyKind", "propertySubkind", "tenure", "currency", "unitOfAreaMeasure", "area", "areaKind", "address", "postalCode", "cityName", "lengthOfAnalysisYears", "lengthOfAnalysisMonths", "assetInfoCompletion", "locationCompletion", "currencyMeasurementCompletion", "valuationDetailsCompletion"]
  static values = { 
    url: String,
    propertyKinds: Object,
    propertySubkindTranslations: Object
  }

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]').content
    
    // Listen for country changes from React component
    this.countryChangedHandler = (e) => {
      this.submitUpdate('country', e.detail.value)
    }
    document.addEventListener('country-changed', this.countryChangedHandler)
  }

  disconnect() {
    // Clean up event listener
    document.removeEventListener('country-changed', this.countryChangedHandler)
  }

  updateField(event) {
    const fieldName = event.target.dataset.fieldName
    const value = event.target.value
    
    this.submitUpdate(fieldName, value)
  }

  updatePropertyKind(event) {
    const propertyKind = event.target.value

    // Reset property_subkind in the UI when property_kind changes
    if (this.hasPropertySubkindTarget) {
      this.propertySubkindTarget.value = ""
      this.updateSubkindOptions(propertyKind)
    }

    // EVA-266: send the new kind and the cleared subkind in a SINGLE request so
    // the backend always sees a coherent pair. Two separate requests could race
    // and trip the property_kind <-> property_subkind consistency check.
    this.submitFields({ property_kind: propertyKind, property_subkind: '' })
  }

  updateSubkindOptions(propertyKind) {
    if (!this.hasPropertySubkindTarget || !propertyKind) {
      // Reset to empty if no property kind selected
      this.propertySubkindTarget.innerHTML = '<option value="">Select property subkind</option>'
      return
    }

    const subkinds = this.propertyKindsValue[propertyKind] || []
    
    // Build new options using translations
    let optionsHtml = '<option value="">Select property subkind</option>'
    subkinds.forEach(subkind => {
      const label = this.propertySubkindTranslationsValue[subkind] || subkind
      optionsHtml += `<option value="${subkind}">${label}</option>`
    })
    
    this.propertySubkindTarget.innerHTML = optionsHtml
  }

  updateValuationMethodologies() {
    // Find all valuation methodology checkboxes
    const checkboxes = document.querySelectorAll('.valuation-methodology-checkbox')
    
    // Calculate bitmask from checked checkboxes
    let mask = 0
    checkboxes.forEach(checkbox => {
      if (checkbox.checked) {
        const bitValue = parseInt(checkbox.dataset.bitValue)
        mask = mask | bitValue
      }
    })
    
    // Submit update with the calculated mask
    this.submitUpdate('valuation_methodology_mask', mask)
  }

  updateLengthOfAnalysis() {
    // Calculate total months from years and months
    const years = parseInt(this.lengthOfAnalysisYearsTarget.value) || 0
    const months = parseInt(this.lengthOfAnalysisMonthsTarget.value) || 0
    
    const totalMonths = (years * 12) + months
    
    this.submitUpdate('length_of_analysis_months', totalMonths)
  }

  async submitUpdate(fieldName, value) {
    return this.submitFields({ [fieldName]: value })
  }

  async submitFields(fields) {
    const data = {
      valuation: fields
    }

    try {
      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify(data)
      })

      const result = await response.json()

      if (result.success) {
        // Update completion badges if available
        if (result.completion) {
          this.updateCompletionBadges(result.completion)
        }
      } else {
        const errorMessages = this.formatErrors(result.errors)
        Alertify.notify(errorMessages, 'error')
      }
    } catch (error) {
      console.error('Error updating valuation:', error)
      Alertify.notify('Failed to update valuation', 'error')
    }
  }

  updateCompletionBadges(completion) {
    const sections = [
      { key: 'asset_info', target: 'assetInfoCompletion' },
      { key: 'location', target: 'locationCompletion' },
      { key: 'currency_measurement', target: 'currencyMeasurementCompletion' },
      { key: 'valuation_details', target: 'valuationDetailsCompletion' }
    ]

    sections.forEach(({ key, target }) => {
      const hasTarget = `has${target.charAt(0).toUpperCase() + target.slice(1)}Target`
      if (this[hasTarget]) {
        const data = completion[key]
        const element = this[`${target}Target`]
        
        // Determine icon based on completion
        const isComplete = data.color === 'success'
        const iconClass = isComplete ? 'bi-check-circle-fill text-success' : 'bi-exclamation-triangle-fill text-warning'
        
        // Update content with icon
        element.innerHTML = `<i class="bi ${iconClass} me-1"></i>${data.completed}/${data.total} completed`
      }
    })
  }

  formatErrors(errors) {
    if (typeof errors === 'string') {
      return errors
    }
    
    if (typeof errors === 'object') {
      return Object.entries(errors)
        .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
        .join('; ')
    }
    
    return 'An error occurred'
  }

  humanize(str) {
    return str
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase())
  }
}

